import axios from 'axios'
import { mockProjectsApi } from './mockProjectsApi'

// Development mode detection - Set to false to use real API
const isDevelopment = false // Changed to use real API
const USE_MOCK_API = false // Now using real API with fixed structure

// Admin API Client
const adminClient = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'https://api.aslanyazilimm.com',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: false // Important for CORS
})

// Request interceptor - Token ekle
adminClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem('admin_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => Promise.reject(error)
)

// Response interceptor - Token geçersizse login'e yönlendir
adminClient.interceptors.response.use(
  response => {
    console.log('API Response SUCCESS:', response.config.url, response.status)
    console.log('Response data:', response.data)
    return response.data
  },
  error => {
    console.error('API Error Details:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.message,
      code: error.code,
      responseData: error.response?.data
    })
    
    if (error.code === 'ERR_NETWORK') {
      console.error('CORS or Network error - API server may be unreachable')
    }
    
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token')
      localStorage.removeItem('admin_user')
      // Login sayfasına yönlendirme event'i
      window.dispatchEvent(new CustomEvent('admin-logout'))
    }
    return Promise.reject(error)
  }
)

export const adminApi = {
  // Authentication
  async login(username, password) {
    try {
      const response = await adminClient.post('/api/admin-auth.php', {
        username,
        password
      })
      return response
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Giriş hatası'
      }
    }
  },

  async logout() {
    try {
      await adminClient.post('/api/admin-auth.php', { action: 'logout' })
    } catch (error) {
      console.log('Logout error:', error)
    } finally {
      localStorage.removeItem('admin_token')
      localStorage.removeItem('admin_user')
    }
  },

  // Dashboard Stats
  async getDashboardStats() {
    return await adminClient.get('/admin/dashboard/stats')
  },

  // News Management
  async getNews(params = {}) {
    return await adminClient.get('/admin/news', { params })
  },

  async createNews(data) {
    return await adminClient.post('/admin/news', data)
  },

  async updateNews(id, data) {
    return await adminClient.put(`/admin/news/${id}`, data)
  },

  async deleteNews(id) {
    return await adminClient.delete(`/admin/news/${id}`)
  },

  // Projects Management
  async getProjects(params = {}) {
    if (USE_MOCK_API) {
      console.log('Using mock API for getProjects')
      return await mockProjectsApi.getProjects(params)
    }
    
    try {
      console.log('Using real API for getProjects')
      console.log('Request params:', params)
      const response = await adminClient.get('/api/admin-projects.php', { params })
      console.log('Raw API response:', response)
      
      // Ensure response has proper structure
      if (response && typeof response === 'object') {
        return response
      } else {
        throw new Error('Invalid response format')
      }
    } catch (error) {
      console.error('Get projects error:', error)
      if (error.code === 'ERR_NETWORK') {
        console.log('Network error - falling back to mock API')
        return await mockProjectsApi.getProjects(params)
      }
      throw error
    }
  },

  async createProject(data) {
    if (USE_MOCK_API) {
      console.log('Using mock API for createProject')
      return await mockProjectsApi.createProject(data)
    }
    
    try {
      console.log('Using real API for createProject')
      const response = await adminClient.post('/api/admin-projects.php', data)
      return response
    } catch (error) {
      console.error('Create project error:', error)
      if (error.code === 'ERR_NETWORK') {
        console.log('Network error - falling back to mock API')
        return await mockProjectsApi.createProject(data)
      }
      throw error
    }
  },

  async updateProject(id, data) {
    if (USE_MOCK_API) {
      console.log('Using mock API for updateProject')
      return await mockProjectsApi.updateProject(id, data)
    }
    
    try {
      console.log('Using real API for updateProject')
      const response = await adminClient.put(`/api/admin-projects.php?action=update&id=${id}`, data)
      return response
    } catch (error) {
      console.error('Update project error:', error)
      if (error.code === 'ERR_NETWORK') {
        console.log('Network error - falling back to mock API')
        return await mockProjectsApi.updateProject(id, data)
      }
      throw error
    }
  },

  async deleteProject(id) {
    if (USE_MOCK_API) {
      console.log('Using mock API for deleteProject')
      return await mockProjectsApi.deleteProject(id)
    }
    
    try {
      console.log('Using real API for deleteProject')
      const response = await adminClient.delete(`/api/admin-projects.php?action=delete&id=${id}`)
      return response
    } catch (error) {
      console.error('Delete project error:', error)
      if (error.code === 'ERR_NETWORK') {
        console.log('Network error - falling back to mock API')
        return await mockProjectsApi.deleteProject(id)
      }
      throw error
    }
  },

  // Services Management
  async getServices(params = {}) {
    try {
      console.log('Using real API for getServices')
      const response = await adminClient.get('/api/admin-services.php', { params })
      return response
    } catch (error) {
      console.error('Get services error:', error)
      throw error
    }
  },

  async createService(serviceData) {
    try {
      console.log('Using real API for createService')
      const response = await adminClient.post('/api/admin-services.php', serviceData)
      return response
    } catch (error) {
      console.error('Create service error:', error)
      throw error
    }
  },

  async updateService(id, serviceData) {
    try {
      console.log('Using real API for updateService')
      // Include id in the request body
      const dataWithId = { ...serviceData, id }
      const response = await adminClient.put('/api/admin-services.php', dataWithId)
      return response
    } catch (error) {
      console.error('Update service error:', error)
      throw error
    }
  },

  async deleteService(id) {
    try {
      console.log('Using real API for deleteService')
      const response = await adminClient.delete('/api/admin-services.php', {
        data: { id }
      })
      return response
    } catch (error) {
      console.error('Delete service error:', error)
      throw error
    }
  },

  // Hero Slides Management
  async getHeroSlides(params = {}) {
    try {
      console.log('Using real API for getHeroSlides')
      const response = await adminClient.get('/api/hero-slides.php', { params })
      return response
    } catch (error) {
      console.error('Get hero slides error:', error)
      throw error
    }
  },

  async createHeroSlide(slideData) {
    try {
      console.log('Using real API for createHeroSlide')
      const response = await adminClient.post('/api/hero-slides.php', slideData)
      return response
    } catch (error) {
      console.error('Create hero slide error:', error)
      throw error
    }
  },

  async updateHeroSlide(id, slideData) {
    try {
      console.log('Using real API for updateHeroSlide')
      // Include id in the request body
      const dataWithId = { ...slideData, id }
      const response = await adminClient.put('/api/hero-slides.php', dataWithId)
      return response
    } catch (error) {
      console.error('Update hero slide error:', error)
      throw error
    }
  },

  async deleteHeroSlide(id) {
    try {
      console.log('Using real API for deleteHeroSlide')
      const response = await adminClient.delete('/api/hero-slides.php', {
        data: { id }
      })
      return response
    } catch (error) {
      console.error('Delete hero slide error:', error)
      throw error
    }
  },

  // About Management
  async getAbout() {
    try {
      console.log('Using real API for getAbout')
      const response = await adminClient.get('/api/admin-about.php')
      return response
    } catch (error) {
      console.error('Get about error:', error)
      throw error
    }
  },

  async updateAbout(aboutData) {
    try {
      console.log('Using real API for updateAbout')
      const response = await adminClient.put('/api/admin-about.php', aboutData)
      return response
    } catch (error) {
      console.error('Update about error:', error)
      throw error
    }
  },

  async createAboutCard(cardData) {
    try {
      console.log('Using real API for createAboutCard')
      const response = await adminClient.post('/api/admin-about.php', cardData)
      return response
    } catch (error) {
      console.error('Create about card error:', error)
      throw error
    }
  },

  async deleteAboutCard(cardId) {
    try {
      console.log('Using real API for deleteAboutCard')
      const response = await adminClient.delete(`/api/admin-about.php?id=${cardId}`)
      return response
    } catch (error) {
      console.error('Delete about card error:', error)
      throw error
    }
  },

  // Statistics Management
  async getStatistics(params = {}) {
    try {
      console.log('Using real API for getStatistics')
      const response = await adminClient.get('/api/admin-statistics.php', { params })
      return response
    } catch (error) {
      console.error('Get statistics error:', error)
      throw error
    }
  },

  async createStatistic(statisticData) {
    try {
      console.log('Using real API for createStatistic')
      const response = await adminClient.post('/api/admin-statistics.php', statisticData)
      return response
    } catch (error) {
      console.error('Create statistic error:', error)
      throw error
    }
  },

  async updateStatistic(id, statisticData) {
    try {
      console.log('Using real API for updateStatistic')
      const dataWithId = { ...statisticData, id }
      const response = await adminClient.put('/api/admin-statistics.php', dataWithId)
      return response
    } catch (error) {
      console.error('Update statistic error:', error)
      throw error
    }
  },

  async updateStatistics(statisticsData) {
    try {
      console.log('Using real API for updateStatistics (bulk)')
      const response = await adminClient.put('/api/admin-statistics.php', statisticsData)
      return response
    } catch (error) {
      console.error('Update statistics error:', error)
      throw error
    }
  },

  async deleteStatistic(id) {
    try {
      console.log('Using real API for deleteStatistic')
      const response = await adminClient.delete(`/api/admin-statistics.php?id=${id}`)
      return response
    } catch (error) {
      console.error('Delete statistic error:', error)
      throw error
    }
  },

  async calculateStatistics() {
    try {
      console.log('Using real API for calculateStatistics')
      const response = await adminClient.get('/api/calculate-statistics.php')
      return response
    } catch (error) {
      console.error('Calculate statistics error:', error)
      throw error
    }
  },

  // Contact Messages
  async getContacts(params = {}) {
    return await adminClient.get('/api/admin-contacts.php', { params })
  },

  async markContactAsRead(id) {
    return await adminClient.put(`/api/admin-contacts.php?action=read&id=${id}`)
  },

  async markContactAsUnread(id) {
    return await adminClient.put(`/api/admin-contacts.php?action=unread&id=${id}`)
  },

  async deleteContact(id) {
    return await adminClient.delete(`/api/admin-contacts.php?action=delete&id=${id}`)
  },

  // News Management
  async getNews(params = {}) {
    return await adminClient.get('/api/admin-news.php', { params })
  },

  async createNews(newsData) {
    return await adminClient.post('/api/admin-news.php', newsData)
  },

  async updateNews(id, newsData) {
    return await adminClient.put(`/api/admin-news.php?action=update&id=${id}`, newsData)
  },

  async deleteNews(id) {
    return await adminClient.delete(`/api/admin-news.php?action=delete&id=${id}`)
  },

  // Machinery Management
  async getMachinery(params = {}) {
    try {
      console.log('Using real API for getMachinery')
      const response = await adminClient.get('/api/admin-machinery.php', { params })
      return response
    } catch (error) {
      console.error('Get machinery error:', error)
      throw error
    }
  },

  async createMachine(machineData) {
    try {
      console.log('Using real API for createMachine')
      const response = await adminClient.post('/api/admin-machinery.php', machineData)
      return response
    } catch (error) {
      console.error('Create machine error:', error)
      throw error
    }
  },

  async updateMachine(id, machineData) {
    try {
      console.log('Using real API for updateMachine')
      const response = await adminClient.put(`/api/admin-machinery.php?action=update&id=${id}`, machineData)
      return response
    } catch (error) {
      console.error('Update machine error:', error)
      throw error
    }
  },

  async deleteMachine(id) {
    try {
      console.log('Using real API for deleteMachine')
      const response = await adminClient.delete(`/api/admin-machinery.php?action=delete&id=${id}`)
      return response
    } catch (error) {
      console.error('Delete machine error:', error)
      throw error
    }
  },

  // Info Cards Management
  async getInfoCards() {
    try {
      console.log('🔄 Getting info cards from admin API')
      const response = await adminClient.get('/api/info-cards.php?admin=true')
      console.log('📊 Raw API response:', response)
      
      // Handle different response structures
      if (response.data && response.data.success) {
        console.log('✅ Successful response with data:', response.data.data)
        return response.data
      } else if (response.data) {
        console.log('📋 Direct data response:', response.data)
        return { success: true, data: response.data }
      } else {
        console.log('⚠️ Unexpected response structure:', response)
        return response
      }
    } catch (error) {
      console.error('❌ Get info cards error:', error)
      throw error
    }
  },

  async createInfoCard(cardData) {
    try {
      console.log('Creating info card via admin API')
      const response = await adminClient.post('/api/info-cards.php', cardData)
      return response.data || response
    } catch (error) {
      console.error('Create info card error:', error)
      throw error
    }
  },

  async updateInfoCard(id, cardData) {
    try {
      console.log('Updating info card via admin API')
      const updateData = { id, ...cardData }
      const response = await adminClient.request({
        method: 'PUT',
        url: '/api/info-cards.php',
        data: updateData
      })
      return response.data || response
    } catch (error) {
      console.error('Update info card error:', error)
      throw error
    }
  },

  async deleteInfoCard(id) {
    try {
      console.log('Deleting info card via admin API')
      const response = await adminClient.request({
        method: 'DELETE',
        url: '/api/info-cards.php',
        data: { id }
      })
      return response.data || response
    } catch (error) {
      console.error('Delete info card error:', error)
      throw error
    }
  },

  // File Upload
  async uploadFile(file, type = 'image') {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', type)

    return await adminClient.post('/admin/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }
}

// Auth utilities
export const adminAuth = {
  isAuthenticated() {
    return !!localStorage.getItem('admin_token')
  },

  getUser() {
    const user = localStorage.getItem('admin_user')
    return user ? JSON.parse(user) : null
  },

  getToken() {
    return localStorage.getItem('admin_token')
  }
}